// src/eh/store.js
// ─────────────────────────────────────────────────────────────────────────────
// Local-first store. THIS is the source of truth for every environmental-health
// record, not Firestore. A field volunteer with no signal must be able to
// capture a full day of work and lose nothing; sync is a background nicety that
// copies records upstream when the network allows (see sync.js).
//
// Storage is IndexedDB (photos are base64 and blow past the ~5MB localStorage
// quota within a dozen reports). localStorage is kept as an automatic fallback
// for browsers/modes where IndexedDB is unavailable, with photos dropped to
// thumbnails so at least the structured record survives.
// ─────────────────────────────────────────────────────────────────────────────

const DB_NAME = "coastclear_eh";
const DB_VERSION = 1;
const LS_PREFIX = "cc_eh_";

export const COLLECTIONS = {
  hazardReports: "hazard_reports",
  surveyResponses: "survey_responses",
  activityLogs: "activity_logs",
  riskZones: "risk_zones",
  messageDeliveries: "message_deliveries",
  notifications: "eh_notifications",
};

const ALL_COLLECTIONS = Object.values(COLLECTIONS);
const META_STORE = "meta";

export const DEFAULT_CONFIG = {
  // Pillar 9c — how long a flood/storm-surge event keeps a zone's disease risk
  // elevated. Configurable because the right window is an epidemiological
  // judgement call, not a constant.
  postFloodWindowDays: 21,
  // Data-quality thresholds for the duplicate check.
  duplicateRadiusM: 120,
  duplicateWindowHours: 24,
  // Hotspot detection (Pillar AI-3).
  hotspotRadiusM: 400,
  hotspotMinReports: 3,
  // Implicit elevation zone drawn around a flood report that falls outside any
  // mapped risk zone.
  eventZoneRadiusM: 1000,
  // Push records to Firestore when online. Off by default: the Firestore rules
  // for this project are not known to be open, and a failing sync must never
  // look like data loss to a volunteer.
  syncEnabled: false,
  // Which version of the sample dataset this device has been seeded with.
  // 0 means never. Compared against DEMO_SEED_VERSION in eh/demoMode.js so the
  // auto-seed runs exactly once per device per version, however many times the
  // app is opened, and so a refreshed sample set can replace an older one.
  demoSeedVersion: 0,
};

// ── ID + time helpers ────────────────────────────────────────────────────────
export const makeId = (prefix = "rec") =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;

const nowISO = () => new Date().toISOString();

// ── IndexedDB plumbing ───────────────────────────────────────────────────────
let dbPromise = null;
let useIndexedDB = typeof indexedDB !== "undefined";

const openDB = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(err);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      ALL_COLLECTIONS.forEach((name) => {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, { keyPath: "id" });
      });
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("IndexedDB blocked"));
  });
  return dbPromise;
};

const idbAll = async (storeName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
};

const idbPut = async (storeName, value) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).put(value);
    tx.oncomplete = () => resolve(value);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
};
// One transaction for a whole batch. Used by the demo seed, which writes ~110
// records at once: a transaction per record makes that visibly slow on the kind
// of phone this app is built for.
const idbPutAll = async (storeName, values) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    values.forEach((value) => store.put(value));
    tx.oncomplete = () => resolve(values);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
};

const idbDelete = async (storeName, id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const idbClear = async (storeName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// ── localStorage fallback ────────────────────────────────────────────────────
// Photos are stripped to a single small thumbnail so a full day's structured
// data still fits inside the quota. The record is what matters most; losing a
// photo is bad, losing the report is unacceptable.
const lsRead = (storeName) => {
  try {
    const raw = localStorage.getItem(LS_PREFIX + storeName);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const lsWrite = (storeName, records) => {
  const slim = records.map((r) =>
    r.photos && r.photos.length
      ? { ...r, photos: r.photos.slice(0, 1).map((p) => ({ ...p, dataUrl: p.thumbUrl || p.dataUrl })) }
      : r
  );
  try {
    localStorage.setItem(LS_PREFIX + storeName, JSON.stringify(slim));
    return true;
  } catch {
    return false;
  }
};

// ── In-memory cache ──────────────────────────────────────────────────────────
// Screens read synchronously from here so the UI never flashes empty while
// IndexedDB resolves. Writes update the cache first, then persist.
const cache = {};
ALL_COLLECTIONS.forEach((c) => {
  cache[c] = [];
});
let config = { ...DEFAULT_CONFIG };
let readyPromise = null;
let lastError = null;

const listeners = new Set();
const notify = () => listeners.forEach((fn) => {
  try {
    fn();
  } catch (err) {
    console.error("EH store listener failed:", err);
  }
});

const loadAll = async () => {
  if (useIndexedDB) {
    try {
      const results = await Promise.all(ALL_COLLECTIONS.map((c) => idbAll(c)));
      ALL_COLLECTIONS.forEach((c, i) => {
        cache[c] = results[i];
      });
      const db = await openDB();
      const meta = await new Promise((resolve) => {
        const tx = db.transaction(META_STORE, "readonly");
        const req = tx.objectStore(META_STORE).get("config");
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      });
      config = { ...DEFAULT_CONFIG, ...(meta?.value || {}) };
      notify();
      return;
    } catch (err) {
      console.warn("EH store: IndexedDB unavailable, falling back to localStorage.", err);
      useIndexedDB = false;
      lastError = err;
    }
  }
  ALL_COLLECTIONS.forEach((c) => {
    cache[c] = lsRead(c);
  });
  try {
    const raw = localStorage.getItem(LS_PREFIX + "config");
    config = { ...DEFAULT_CONFIG, ...(raw ? JSON.parse(raw) : {}) };
  } catch (err) {
    // Corrupt or unreadable config. Defaults are the right recovery, but it has
    // to be visible: this silently resets the post-flood window and the sync
    // switch, and demoSeedVersion going back to 0 would re-offer the seed.
    console.warn("EH store: stored config could not be read, using defaults.", err);
    config = { ...DEFAULT_CONFIG };
  }
  notify();
};

const persist = async (storeName, record) => {
  if (useIndexedDB) {
    try {
      await idbPut(storeName, record);
      return true;
    } catch (err) {
      console.warn("EH store: write to IndexedDB failed, falling back.", err);
      useIndexedDB = false;
      lastError = err;
    }
  }
  const ok = lsWrite(storeName, cache[storeName]);
  if (!ok) lastError = new Error("Local storage is full — export and clear synced records.");
  return ok;
};

const persistAll = async (storeName, records) => {
  if (useIndexedDB) {
    try {
      await idbPutAll(storeName, records);
      return true;
    } catch (err) {
      console.warn("EH store: batch write to IndexedDB failed, falling back.", err);
      useIndexedDB = false;
      lastError = err;
    }
  }
  const ok = lsWrite(storeName, cache[storeName]);
  if (!ok) lastError = new Error("Local storage is full — export and clear synced records.");
  return ok;
};

const persistConfig = async () => {
  if (useIndexedDB) {
    try {
      const db = await openDB();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(META_STORE, "readwrite");
        tx.objectStore(META_STORE).put({ key: "config", value: config });
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
      return;
    } catch (err) {
      useIndexedDB = false;
      lastError = err;
    }
  }
  try {
    localStorage.setItem(LS_PREFIX + "config", JSON.stringify(config));
  } catch (err) {
    lastError = err;
  }
};

// ── Public API ───────────────────────────────────────────────────────────────
export const ehStore = {
  ready() {
    if (!readyPromise) readyPromise = loadAll();
    return readyPromise;
  },

  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  /** Synchronous read of the in-memory cache. */
  all(collection) {
    return cache[collection] || [];
  },

  get(collection, id) {
    return (cache[collection] || []).find((r) => r.id === id) || null;
  },

  /**
   * Insert or update. New records are stamped with an id, timestamps and a
   * `pending` sync state. Returns the stored record.
   */
  async put(collection, record) {
    const existing = record.id ? this.get(collection, record.id) : null;
    const stored = {
      ...record,
      id: record.id || makeId(collection.slice(0, 4)),
      createdAt: existing?.createdAt || record.createdAt || nowISO(),
      updatedAt: nowISO(),
      // A record that changed after being synced goes back to pending so the
      // update is pushed too.
      syncState: record.syncState === "synced" ? "synced" : "pending",
      syncError: null,
    };
    const idx = (cache[collection] || []).findIndex((r) => r.id === stored.id);
    if (idx >= 0) cache[collection][idx] = stored;
    else cache[collection] = [stored, ...(cache[collection] || [])];
    await persist(collection, stored);
    notify();
    return stored;
  },

  /**
   * Insert many records into one collection in a single transaction.
   *
   * Same stamping rules as put(), but it notifies subscribers once at the end
   * instead of once per record. The demo seed writes about 110 records; going
   * through put() meant 110 transactions and 110 re-renders, which is seconds
   * of frozen UI on the low-end Android this app targets.
   *
   * Intended for bulk inserts of new records. Use put() for anything that
   * needs the return value or might be updating an existing row.
   */
  async putMany(collection, records) {
    if (!records?.length) return [];
    const stamped = records.map((record) => ({
      ...record,
      id: record.id || makeId(collection.slice(0, 4)),
      createdAt: record.createdAt || nowISO(),
      updatedAt: nowISO(),
      syncState: record.syncState === "synced" ? "synced" : "pending",
      syncError: null,
    }));
    cache[collection] = [...stamped, ...(cache[collection] || [])];
    await persistAll(collection, stamped);
    notify();
    return stamped;
  },

  /** Shallow-merge a patch into an existing record. */
  async patch(collection, id, patch) {
    const existing = this.get(collection, id);
    if (!existing) return null;
    return this.put(collection, { ...existing, ...patch });
  },

  /** Used by sync.js only — marks state without bouncing back to pending. */
  async markSync(collection, id, syncState, extra = {}) {
    const existing = this.get(collection, id);
    if (!existing) return null;
    const stored = { ...existing, ...extra, syncState, updatedAt: existing.updatedAt };
    const idx = cache[collection].findIndex((r) => r.id === id);
    cache[collection][idx] = stored;
    await persist(collection, stored);
    notify();
    return stored;
  },

  async remove(collection, id) {
    cache[collection] = (cache[collection] || []).filter((r) => r.id !== id);
    if (useIndexedDB) {
      try {
        await idbDelete(collection, id);
      } catch {
        lsWrite(collection, cache[collection]);
      }
    } else {
      lsWrite(collection, cache[collection]);
    }
    notify();
  },

  async clear(collection) {
    cache[collection] = [];
    if (useIndexedDB) {
      try {
        await idbClear(collection);
      } catch {
        lsWrite(collection, []);
      }
    } else {
      lsWrite(collection, []);
    }
    notify();
  },

  async clearAll() {
    for (const c of ALL_COLLECTIONS) await this.clear(c);
  },

  getConfig() {
    return config;
  },

  async setConfig(patch) {
    config = { ...config, ...patch };
    await persistConfig();
    notify();
    return config;
  },

  /** Queue health, shown in the sync banner. */
  syncStats() {
    let pending = 0;
    let synced = 0;
    let error = 0;
    ALL_COLLECTIONS.forEach((c) => {
      (cache[c] || []).forEach((r) => {
        if (r.syncState === "synced") synced += 1;
        else if (r.syncState === "error") error += 1;
        else pending += 1;
      });
    });
    return { pending, synced, error, total: pending + synced + error };
  },

  totalRecords() {
    return ALL_COLLECTIONS.reduce((n, c) => n + (cache[c] || []).length, 0);
  },

  storageMode() {
    return useIndexedDB ? "indexeddb" : "localstorage";
  },

  lastError() {
    return lastError;
  },
};

// ── Geo helpers ──────────────────────────────────────────────────────────────
/** Great-circle distance in metres. */
export const distanceM = (lat1, lng1, lat2, lng2) => {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
};

/**
 * Browser GPS with a sane timeout. Never rejects — the caller gets a result
 * object either way, because a missing fix must not block a field report.
 */
export const captureGPS = (timeoutMs = 12000) =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ ok: false, reason: "unsupported" });
      return;
    }
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve({ ok: false, reason: "timeout" });
      }
    }, timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({
          ok: true,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: pos.coords.accuracy,
        });
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({ ok: false, reason: err.code === 1 ? "denied" : "unavailable" });
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30000 }
    );
  });

/**
 * Downscale a captured photo before it enters the store. Field phones produce
 * 4–8MB JPEGs; unshrunk they exhaust the storage quota after a handful of
 * reports and take forever to sync over a 2G link.
 */
export const processPhoto = (file, maxDim = 1280, quality = 0.72) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the photo file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode the photo."));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);

        const thumb = document.createElement("canvas");
        const tScale = Math.min(1, 240 / Math.max(img.width, img.height));
        thumb.width = Math.round(img.width * tScale);
        thumb.height = Math.round(img.height * tScale);
        thumb.getContext("2d").drawImage(img, 0, 0, thumb.width, thumb.height);

        resolve({
          dataUrl: canvas.toDataURL("image/jpeg", quality),
          thumbUrl: thumb.toDataURL("image/jpeg", 0.6),
          w,
          h,
          takenAt: nowISO(),
          originalName: file.name || "photo.jpg",
          originalBytes: file.size || null,
        });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
